import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { MapPin, X } from 'lucide-react';
import {
  Field,
  TextInput,
  SelectInput,
  FormSection,
  PrimaryButton,
  GhostButton,
  Switch,
} from '../ui/Field';
import { COUNTRIES, getStatesProvinces } from '../../data/geo-data';
import type { Site, Company } from '../../types/api';

// ─────────────────────────────────────────────────────────────
// SiteForm — Operations Deck redesign
//
// Sections:
//   01 · Ownership      (company, surface location, site #)
//   02 · Location       (country/state cascade, city, postal, address)
//   03 · Coordinates    (lat / long)
//
// Matches CompanyForm shell & primitives so the visual language
// stays consistent across the Operations Deck.
// ─────────────────────────────────────────────────────────────

const emptySite: Partial<Site> = {
  siteName: '',
  siteNumber: '',
  companyId: 0,
  address: '',
  city: '',
  stateProvince: '',
  postalZipCode: '',
  country: '',
  latitude: '',
  longitude: '',
  isDefaultSite: false,
};

export interface SiteErrors {
  companyId?: string;
  siteName?: string;
  country?: string;
  stateProvince?: string;
}

function validate(form: Partial<Site>): SiteErrors {
  const e: SiteErrors = {};
  if (!form.companyId) e.companyId = 'Company is required';
  if (!form.siteName?.trim()) e.siteName = 'Surface Location is required';
  if (!form.country?.trim()) e.country = 'Country is required';
  if (!form.stateProvince?.trim()) e.stateProvince = 'State / Province is required';
  return e;
}

interface SiteFormProps {
  open: boolean;
  editing: Site | null;
  saving: boolean;
  companies: Company[];
  /** When the user is not SuperAdmin we lock company to this id. */
  lockedCompanyId?: number;
  onCancel: () => void;
  onSave: (form: Partial<Site>) => Promise<void> | void;
}

export function SiteForm({
  open,
  editing,
  saving,
  companies,
  lockedCompanyId,
  onCancel,
  onSave,
}: SiteFormProps) {
  const [form, setForm] = useState<Partial<Site>>(emptySite);
  const [errors, setErrors] = useState<SiteErrors>({});
  const [submitted, setSubmitted] = useState(false);

  // Reset/hydrate when modal opens or target changes.
  useEffect(() => {
    if (!open) return;
    setForm(
      editing
        ? { ...editing }
        : { ...emptySite, companyId: lockedCompanyId ?? 0 },
    );
    setErrors({});
    setSubmitted(false);
  }, [open, editing, lockedCompanyId]);

  // Lock background scroll while open.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Escape closes; auto-focus first field on open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) onCancel();
    };
    document.addEventListener('keydown', onKey);
    const t = setTimeout(() => {
      const el = document.querySelector<HTMLInputElement>('[data-field="siteName"] input');
      el?.focus();
    }, 60);
    return () => {
      document.removeEventListener('keydown', onKey);
      clearTimeout(t);
    };
  }, [open, saving, onCancel]);

  if (!open) return null;

  const set = <K extends keyof Site>(key: K, value: Site[K]) =>
    setForm((p) => ({ ...p, [key]: value }));

  const onCountryChange = (country: string) => {
    setForm((p) => ({ ...p, country, stateProvince: '' }));
    if (submitted) {
      setErrors(validate({ ...form, country, stateProvince: '' }));
    }
  };

  const states = getStatesProvinces(form.country ?? '');
  const hasStateList = states.length > 0;

  const handleSubmit = async () => {
    setSubmitted(true);
    const e = validate(form);
    setErrors(e);
    if (Object.keys(e).length > 0) {
      const firstKey = Object.keys(e)[0];
      const el = document.querySelector<HTMLElement>(`[data-field="${firstKey}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    await onSave(form);
  };

  const liveValidate = (next: Partial<Site>) => {
    if (submitted) setErrors(validate(next));
  };

  const subtitle = editing ? `Locations · Editing #${editing.id ?? ''}` : 'Locations · New entry';
  const title = editing ? 'Edit location' : 'Add location';
  const errorCount = Object.keys(errors).length;
  const companyLocked = !!lockedCompanyId;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="site-form-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        background: 'rgba(15, 23, 32, 0.45)',
        backdropFilter: 'blur(2px)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '5vh 16px 5vh',
        overflowY: 'auto',
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !saving) onCancel();
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 760,
          background: 'var(--color-panel)',
          borderRadius: 14,
          boxShadow: '0 32px 80px rgba(0,0,0,0.30)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '90vh',
        }}
      >
        {/* ── Header ─────────────────────────────────────────── */}
        <header
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 16,
            padding: '20px 24px 18px',
            borderBottom: '1px solid var(--color-border)',
          }}
        >
          <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', minWidth: 0 }}>
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 9,
                background: 'rgba(11,74,111,0.10)',
                color: 'var(--color-accent)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                marginTop: 2,
              }}
            >
              <MapPin size={20} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 10.5,
                  textTransform: 'uppercase',
                  letterSpacing: 1.4,
                  color: 'var(--color-text-dim)',
                  fontFamily: 'var(--font-mono)',
                  fontWeight: 500,
                }}
              >
                {subtitle}
              </div>
              <h2
                id="site-form-title"
                style={{
                  fontFamily: 'var(--font-display)',
                  fontSize: 24,
                  fontWeight: 500,
                  letterSpacing: -0.4,
                  color: 'var(--color-text)',
                  marginTop: 3,
                  marginBottom: 4,
                  lineHeight: 1.2,
                }}
              >
                {title}
              </h2>
              <p
                style={{
                  fontSize: 13,
                  color: 'var(--color-text-mid)',
                  margin: 0,
                  lineHeight: 1.45,
                  maxWidth: 520,
                }}
              >
                Surface location where wells can be drilled. Fields marked with{' '}
                <span style={{ color: 'var(--color-bad)' }}>*</span> are required.
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10, flexShrink: 0 }}>
            <button
              onClick={onCancel}
              aria-label="Close"
              disabled={saving}
              style={{
                width: 30,
                height: 30,
                borderRadius: 6,
                background: 'transparent',
                border: 'none',
                color: 'var(--color-text-dim)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-panel-alt)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <X size={18} />
            </button>
            <Switch
              checked={!!form.isDefaultSite}
              onChange={(v) => set('isDefaultSite', v)}
              label={form.isDefaultSite ? 'Default location' : 'Standard'}
              description={form.isDefaultSite ? 'Pre-selected for new wells' : 'One per company'}
            />
          </div>
        </header>

        {/* ── Error summary ──────────────────────────────────── */}
        {submitted && errorCount > 0 && (
          <div
            role="alert"
            style={{
              padding: '10px 24px',
              background: 'rgba(185,28,28,0.06)',
              borderBottom: '1px solid rgba(185,28,28,0.15)',
              color: 'var(--color-bad)',
              fontSize: 12.5,
              fontFamily: 'var(--font-body)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <strong style={{ fontWeight: 600 }}>
              {errorCount} field{errorCount === 1 ? '' : 's'} need
              {errorCount === 1 ? 's' : ''} attention
            </strong>
            <span style={{ color: 'var(--color-text-mid)' }}>— please review the highlighted inputs.</span>
          </div>
        )}

        {/* ── Scrollable body ───────────────────────────────── */}
        <div
          style={{
            padding: '22px 24px 24px',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 26,
            background: 'var(--color-panel)',
          }}
        >
          {/* 01 Ownership */}
          <FormSection number={1} title="Ownership">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Company"
                required
                error={submitted ? errors.companyId : undefined}
                style={{ gridColumn: '1 / -1' } as never}
              >
                <div data-field="companyId">
                  {companyLocked ? (
                    <TextInput
                      value={
                        companies.find((c) => c.id === form.companyId)?.companyName ??
                        `Company #${form.companyId}`
                      }
                      readOnly
                      style={{
                        background: 'var(--color-panel-alt)',
                        color: 'var(--color-text-mid)',
                        cursor: 'not-allowed',
                      }}
                    />
                  ) : (
                    <SelectInput
                      value={form.companyId ?? ''}
                      error={submitted && !!errors.companyId}
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        set('companyId', v);
                        liveValidate({ ...form, companyId: v });
                      }}
                    >
                      <option value="">— Select company —</option>
                      {companies.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.companyName}
                        </option>
                      ))}
                    </SelectInput>
                  )}
                </div>
              </Field>
              <Field
                label="Surface Location"
                required
                error={submitted ? errors.siteName : undefined}
              >
                <div data-field="siteName">
                  <TextInput
                    value={form.siteName ?? ''}
                    placeholder="e.g. Wolfcamp Pad A"
                    error={submitted && !!errors.siteName}
                    onChange={(e) => {
                      set('siteName', e.target.value);
                      liveValidate({ ...form, siteName: e.target.value });
                    }}
                  />
                </div>
              </Field>
              <Field label="Site number" hint="Optional reference / pad ID.">
                <TextInput
                  value={form.siteNumber ?? ''}
                  placeholder="e.g. WC-12"
                  onChange={(e) => set('siteNumber', e.target.value)}
                />
              </Field>
            </div>
          </FormSection>

          {/* 02 Location */}
          <FormSection number={2} title="Location">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Country" required error={submitted ? errors.country : undefined}>
                <div data-field="country">
                  <SelectInput
                    value={form.country ?? ''}
                    error={submitted && !!errors.country}
                    onChange={(e) => onCountryChange(e.target.value)}
                  >
                    <option value="">— Select country —</option>
                    {COUNTRIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </SelectInput>
                </div>
              </Field>
              <Field
                label="State / Province"
                required
                error={submitted ? errors.stateProvince : undefined}
              >
                <div data-field="stateProvince">
                  {hasStateList ? (
                    <SelectInput
                      value={form.stateProvince ?? ''}
                      error={submitted && !!errors.stateProvince}
                      onChange={(e) => {
                        set('stateProvince', e.target.value);
                        liveValidate({ ...form, stateProvince: e.target.value });
                      }}
                    >
                      <option value="">— Select state / province —</option>
                      {states.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </SelectInput>
                  ) : (
                    <TextInput
                      value={form.stateProvince ?? ''}
                      placeholder={form.country ? 'Enter state / province' : 'Select country first'}
                      disabled={!form.country}
                      error={submitted && !!errors.stateProvince}
                      onChange={(e) => {
                        set('stateProvince', e.target.value);
                        liveValidate({ ...form, stateProvince: e.target.value });
                      }}
                    />
                  )}
                </div>
              </Field>
              <Field label="City">
                <TextInput
                  value={form.city ?? ''}
                  placeholder="e.g. Midland"
                  onChange={(e) => set('city', e.target.value)}
                />
              </Field>
              <Field label="Postal / Zip">
                <TextInput
                  value={form.postalZipCode ?? ''}
                  placeholder="T2P 1J9 · 79701"
                  onChange={(e) => set('postalZipCode', e.target.value)}
                />
              </Field>
              <Field label="Street address" style={{ gridColumn: '1 / -1' } as never}>
                <TextInput
                  value={form.address ?? ''}
                  placeholder="123 Lease Rd"
                  onChange={(e) => set('address', e.target.value)}
                />
              </Field>
            </div>
          </FormSection>

          {/* 03 Coordinates */}
          <FormSection number={3} title="Coordinates" highlight>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Latitude" hint="Decimal degrees, e.g. 31.9974">
                <TextInput
                  value={form.latitude ?? ''}
                  placeholder="31.9974"
                  onChange={(e) => set('latitude', e.target.value)}
                />
              </Field>
              <Field label="Longitude" hint="Decimal degrees, e.g. -102.0779">
                <TextInput
                  value={form.longitude ?? ''}
                  placeholder="-102.0779"
                  onChange={(e) => set('longitude', e.target.value)}
                />
              </Field>
            </div>
          </FormSection>
        </div>

        {/* ── Sticky footer ──────────────────────────────────── */}
        <footer
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: '14px 24px',
            background: 'var(--color-panel-alt)',
            borderTop: '1px solid var(--color-border)',
          }}
        >
          <div style={{ fontSize: 11.5, color: 'var(--color-text-dim)', fontFamily: 'var(--font-mono)', letterSpacing: 0.6 }}>
            {editing ? `LAST UPDATE · ${formatTs(editing.lastModifiedDate ?? editing.createdDate)}` : 'NEW RECORD'}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <GhostButton onClick={onCancel} disabled={saving}>
              Cancel
            </GhostButton>
            <PrimaryButton onClick={handleSubmit} disabled={saving}>
              {saving ? 'Saving…' : editing ? 'Save changes' : 'Add location'}
            </PrimaryButton>
          </div>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

function formatTs(d?: string | null): string {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}
